/**
 * ExecutiveSummaryStripe — Bloomberg-Terminal-style dominant header.
 *
 * Surfaces the seven pieces of decision-driving information at-a-glance:
 *   • Current workflow stage
 *   • NTN Recommendation (Recommended / Recommended with Conditions / Not Recommended / Pending)
 *   • Risk Indicators (open count + intent)
 *   • Verification progress (completed reports)
 *   • Waiting-On indicator
 *   • Decision-ready status
 *
 * Renders as a single dense band at the top of Applicant + Household profiles.
 * Self-contained: fetches the risk-indicator open count internally so it can
 * be dropped into any page without props plumbing.
 */
import { useEffect, useState } from "react";
import { apiClient } from "@/lib/api";
import { stageFor, PIPELINE_STAGES } from "@/lib/verifyHub";
import {
  ShieldAlert, ShieldCheck, Gavel, CheckCircle2, Hourglass, Activity,
  AlertTriangle, Sparkles, Award, XCircle, MinusCircle, Archive,
} from "lucide-react";

const STAGE_TONE_DOT = {
  neutral: "bg-slate-400",
  amber:   "bg-amber-400",
  violet:  "bg-violet-400",
  blue:    "bg-blue-400",
  emerald: "bg-emerald-400",
};

const REC_STATE = {
  approve:     { label: "Recommended",                 cls: "bg-emerald-50 text-emerald-700 ring-emerald-200", dot: "bg-emerald-500", icon: CheckCircle2 },
  conditional: { label: "Recommended with Conditions", cls: "bg-amber-50 text-amber-700 ring-amber-200",       dot: "bg-amber-500",   icon: AlertTriangle },
  deny:        { label: "Not Recommended",             cls: "bg-rose-50 text-rose-700 ring-rose-200",          dot: "bg-rose-500",    icon: Gavel },
};

// PM Final Decision states — closing outcomes that supersede recommendation.
const FINAL_STATE = {
  approved:     { label: "Approved",     dot: "bg-emerald-400", icon: Award,       tone: "emerald" },
  denied:       { label: "Not Approved", dot: "bg-rose-400",    icon: XCircle,     tone: "rose" },
  not_selected: { label: "Not Selected", dot: "bg-slate-400",   icon: MinusCircle, tone: "slate" },
};

const WAITING_LABEL = {
  applicant: "Waiting on Applicant",
  employer: "Waiting on Employer",
  landlord: "Waiting on Landlord",
  property_manager: "Waiting on Property Manager",
  other: "Waiting on External Source",
};

export default function ExecutiveSummaryStripe({ screening, testid = "executive-summary" }) {
  const s = screening;
  const [openIndicators, setOpenIndicators] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await apiClient.get(`/screenings/${s.id}/risk-indicators`);
        if (cancelled) return;
        const open = (data || []).filter((r) => ["open", "under_review", "reviewed"].includes(r.status)).length;
        setOpenIndicators(open);
      } catch {
        if (!cancelled) setOpenIndicators(0);
      }
    })();
    return () => { cancelled = true; };
  }, [s.id]);

  const stage = stageFor(s);
  const stageIdx = PIPELINE_STAGES.findIndex((p) => p.key === stage.key);
  const requested = s.reports.filter((r) => r.requested);
  const completed = requested.filter((r) => r.status === "complete").length;
  const recState = s.recommendation && REC_STATE[s.recommendation];
  const finalState = s.final_decision && FINAL_STATE[s.final_decision];

  const isDecisionReady = s.overall_status === "complete";
  const waitingLabel = s.additional_info?.waiting_on ? WAITING_LABEL[s.additional_info.waiting_on] : null;

  return (
    <div className="bg-[#0A192F] text-white rounded-lg overflow-hidden shadow-lg" data-testid={testid}>
      {/* Top status bar — eyebrow + applicant name */}
      <div className="px-5 py-3 border-b border-white/8 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2.5">
          <Sparkles className="h-3.5 w-3.5 text-amber-300" />
          <span className="ntn-eyebrow text-white/70">
            Executive Summary
          </span>
        </div>
        <div className="ntn-mono text-white/50">
          ID · {s.id.slice(0, 8).toUpperCase()}
        </div>
      </div>

      {/* Tile grid — 5 dense columns */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 divide-y md:divide-y-0 md:divide-x divide-white/8">
        {/* 1 · Stage */}
        <Tile
          eyebrow="Workflow Stage"
          icon={Activity}
          testid="exec-stage"
          accent={STAGE_TONE_DOT[stage.tone] || "bg-slate-400"}
        >
          <div className="font-serif text-lg leading-tight" data-testid="exec-stage-label">{stage.label}</div>
          <div className="text-[10px] text-white/60 mt-1 font-mono">
            {stageIdx >= 0 ? `Stage ${stageIdx + 1} of 6` : "—"}
          </div>
        </Tile>

        {/* 2 · NTN Recommendation */}
        <Tile
          eyebrow="NTN Recommendation"
          icon={recState?.icon || Gavel}
          testid="exec-recommendation"
          accent={recState?.dot || "bg-white/30"}
        >
          {recState ? (
            <>
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md ring-1 ring-inset font-semibold text-xs uppercase tracking-wider mt-0.5" data-testid={`exec-rec-${s.recommendation}`}
                style={{ background: "rgba(255,255,255,0.06)" }}>
                <span className={`h-1.5 w-1.5 rounded-full ${recState.dot}`} />
                {recState.label}
              </div>
              {s.recommendation_by_name && (
                <div className="text-[10px] text-white/50 mt-1.5 font-mono">by {s.recommendation_by_name}</div>
              )}
            </>
          ) : (
            <>
              <div className="font-serif text-lg leading-tight text-white/80" data-testid="exec-rec-pending">
                {isDecisionReady ? "Decision-Ready Review" : "Pending"}
              </div>
              <div className="text-[10px] text-white/60 mt-1 font-mono">
                {isDecisionReady ? "Awaiting NTN review" : "Reports in progress"}
              </div>
            </>
          )}
        </Tile>

        {/* 3 · Risk Indicators */}
        <Tile
          eyebrow="Risk Indicators"
          icon={openIndicators > 0 ? ShieldAlert : ShieldCheck}
          testid="exec-risk"
          accent={openIndicators > 0 ? "bg-amber-400" : "bg-emerald-400"}
        >
          {openIndicators === null ? (
            <div className="text-white/40 text-sm">…</div>
          ) : openIndicators > 0 ? (
            <>
              <div className="flex items-baseline gap-1.5">
                <span className="font-serif text-3xl text-amber-300" data-testid="exec-risk-count">{openIndicators}</span>
                <span className="text-xs text-white/70">requiring review</span>
              </div>
              <div className="text-[10px] text-white/60 mt-1 font-mono uppercase tracking-wider">Risk Indicators Identified</div>
            </>
          ) : (
            <>
              <div className="font-serif text-lg leading-tight text-emerald-300" data-testid="exec-risk-none">None Identified</div>
              <div className="text-[10px] text-white/60 mt-1 font-mono uppercase tracking-wider">Clean Profile</div>
            </>
          )}
        </Tile>

        {/* 4 · Verification Progress */}
        <Tile
          eyebrow="Verification Progress"
          icon={CheckCircle2}
          testid="exec-verification"
          accent={requested.length > 0 && completed === requested.length ? "bg-emerald-400" : "bg-blue-400"}
        >
          <div className="flex items-baseline gap-1.5">
            <span className="font-serif text-3xl text-white" data-testid="exec-verification-count">
              {completed}<span className="text-white/40 text-xl">/{requested.length}</span>
            </span>
          </div>
          <div className="text-[10px] text-white/60 mt-1 font-mono uppercase tracking-wider">Reports Complete</div>
          <div className="mt-2 h-1 w-full bg-white/8 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all ${requested.length > 0 && completed === requested.length ? "bg-emerald-400" : "bg-blue-400"}`}
              style={{ width: `${requested.length ? (completed / requested.length) * 100 : 0}%` }}
            />
          </div>
        </Tile>

        {/* 5 · Status — Final Decision (if closed) | Waiting | Decision-Ready */}
        <Tile
          eyebrow="Status"
          icon={finalState ? finalState.icon : (waitingLabel ? Hourglass : (isDecisionReady ? Gavel : Activity))}
          testid="exec-status"
          accent={finalState ? finalState.dot : (waitingLabel ? "bg-violet-400" : (isDecisionReady ? "bg-amber-400" : "bg-slate-400"))}
        >
          {finalState ? (
            <>
              <div className={`font-serif text-base leading-tight ${finalState.tone === "emerald" ? "text-emerald-300" : finalState.tone === "rose" ? "text-rose-300" : "text-slate-200"}`}
                   data-testid={`exec-final-${s.final_decision}`}>
                {finalState.label}
              </div>
              <div className="text-[10px] text-white/60 mt-1 font-mono uppercase tracking-wider flex items-center gap-1">
                <Archive className="h-2.5 w-2.5" /> Closed & Archived
              </div>
            </>
          ) : waitingLabel ? (
            <>
              <div className="font-serif text-base leading-tight text-violet-200" data-testid="exec-status-waiting">{waitingLabel}</div>
              <div className="text-[10px] text-white/60 mt-1 font-mono uppercase tracking-wider">Additional Info Needed</div>
            </>
          ) : isDecisionReady && !recState ? (
            <>
              <div className="font-serif text-base leading-tight text-amber-200" data-testid="exec-status-ready">Decision-Ready Review</div>
              <div className="text-[10px] text-white/60 mt-1 font-mono uppercase tracking-wider">Awaiting Recommendation</div>
            </>
          ) : recState ? (
            <>
              <div className="font-serif text-base leading-tight text-white">Decision Recorded</div>
              <div className="text-[10px] text-white/60 mt-1 font-mono uppercase tracking-wider">Awaiting PM Final Decision</div>
            </>
          ) : (
            <>
              <div className="font-serif text-base leading-tight text-white/80">In Progress</div>
              <div className="text-[10px] text-white/60 mt-1 font-mono uppercase tracking-wider">Tracking</div>
            </>
          )}
        </Tile>
      </div>
    </div>
  );
}

function Tile({ eyebrow, icon: Icon, accent, testid, children }) {
  return (
    <div className="relative px-5 py-4" data-testid={testid}>
      <div className={`absolute left-0 top-4 bottom-4 w-0.5 ${accent} rounded-r-full opacity-80`} />
      <div className="flex items-center gap-1.5 mb-2">
        <Icon className="h-3 w-3 text-white/50" />
        <span className="ntn-eyebrow text-white/60" style={{ fontSize: "0.625rem" }}>{eyebrow}</span>
      </div>
      {children}
    </div>
  );
}
